/**
 * DOMI-CARE - Módulo 4: Logística, Equipamiento e Insumos Médicos Domiciliarios
 * Seguimiento de aparatología en comodato, stock de descartables/farmacia y gestión de ambulancias.
 */

import { offlineStore } from '../../api/offlineStore.js';
import { SEED_DATA } from '../../data/seedData.js';
import { notify } from '../../components/notificationCenter.js';

export async function renderLogisticaView(container, activePatientId) {
  const allEquipos = await offlineStore.getAll('equipamiento');
  const allInsumos = await offlineStore.getAll('insumos');
  const traslados = SEED_DATA.traslados;

  // Filtrar o destacar para el paciente activo
  const equiposPaciente = allEquipos.filter(e => e.patientId === activePatientId);
  const insumosPaciente = allInsumos.filter(i => i.patientId === activePatientId);

  container.innerHTML = `
    <div class="view-header">
      <div>
        <h1 class="view-title">Logística, Equipamiento e Insumos Médicos</h1>
        <p class="view-subtitle">Monitoreo de aparatología en comodato, farmacia en domicilio y traslados sanitarios</p>
      </div>
      <div class="view-actions">
        <button id="btn-solicitar-traslado" class="btn btn-danger btn-sm">
          🚑 Solicitar Ambulancia / Traslado
        </button>
        <button id="btn-nuevo-envio" class="btn btn-primary btn-sm">
          📦 Programar Envío de Farmacia
        </button>
      </div>
    </div>

    <!-- 1. ALERTAS DE REPOSICIÓN PREVENTIVA -->
    <div class="card" style="margin-bottom: 1.5rem; background: #fffdf5; border-left: 4px solid var(--warning-500);">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
        <div style="font-weight: 700; color: var(--warning-800); display: flex; align-items: center; gap: 0.5rem;">
          ⚠️ Centro de Alerta Preventiva de Stock en Domicilio
        </div>
        <span class="badge badge-warning">Algoritmo de Consumo Activo</span>
      </div>
      <p style="font-size: 0.85rem; color: var(--slate-700); margin-bottom: 0.75rem;">
        El sistema calcula el consumo diario estimado y emite advertencias antes del agotamiento para garantizar continuidad asistencial.
      </p>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 0.75rem;">
        ${allInsumos.filter(i => i.severidad === 'danger' || i.severidad === 'warning').map(i => `
          <div style="background: #ffffff; padding: 0.75rem; border-radius: var(--radius-md); border: 1px solid var(--border-subtle); display: flex; justify-content: space-between; align-items: center;">
            <div>
              <div style="font-weight: 700; font-size: 0.9rem; color: var(--text-primary);">${i.nombre}</div>
              <div style="font-size: 0.75rem; color: var(--text-muted);">Stock actual: ${i.stockActual} unidades (${i.presentacion})</div>
            </div>
            <span class="badge ${i.severidad === 'danger' ? 'badge-danger' : 'badge-warning'}">
              ${i.diasDisponibles} días restantes
            </span>
          </div>
        `).join('')}
      </div>
    </div>

    <!-- 2. APARATOLOGÍA EN COMODATO -->
    <div class="card" style="margin-bottom: 2rem;">
      <div class="card-header">
        <h3 class="card-title">
          🛏️ Inventario de Aparatología y Equipamiento Prestado (${allEquipos.length})
        </h3>
        <span class="badge badge-primary">Mantenimiento Preventivo</span>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Equipo / Aparato</th>
              <th>Paciente Asignado</th>
              <th>Marca y Modelo</th>
              <th>Número de Serie</th>
              <th>Entrega / Remito</th>
              <th>Próximo Service</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            ${allEquipos.map(eq => `
              <tr style="${eq.patientId === activePatientId ? 'background: rgba(2, 132, 199, 0.05); font-weight: 600;' : ''}">
                <td>
                  <strong>${eq.tipo}</strong>
                  ${eq.patientId === activePatientId ? '<span class="badge badge-primary" style="margin-left: 0.4rem; font-size: 0.65rem;">Paciente en Foco</span>' : ''}
                </td>
                <td>${eq.pacienteNombre}</td>
                <td>${eq.marca}</td>
                <td><code style="font-family: var(--font-mono); font-size: 0.8rem; background: var(--slate-100); padding: 0.2rem 0.4rem; border-radius: 4px;">${eq.numeroSerie}</code></td>
                <td>${eq.fechaEntrega} <span style="font-size: 0.7rem; color: var(--text-muted);">(${eq.remito})</span></td>
                <td><span style="color: var(--primary-700); font-weight: 600;">${eq.proximoService}</span></td>
                <td><span class="badge badge-success">${eq.estado}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>

    <!-- 3. STOCK DE FARMACIA Y DESCARTABLES EN DOMICILIO -->
    <div class="card" style="margin-bottom: 2rem;">
      <div class="card-header">
        <h3 class="card-title">
          💊 Control de Stock de Farmacia y Descartables en Domicilio
        </h3>
        <button class="btn btn-secondary btn-sm" id="btn-registrar-consumo">
          - Registrar Consumo Diario
        </button>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Insumo Médico / Medicación</th>
              <th>Presentación</th>
              <th>Stock en Domicilio</th>
              <th>Consumo Diario</th>
              <th>Días de Cobertura</th>
              <th>Estado de Alerta</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            ${allInsumos.map(ins => `
              <tr style="${ins.patientId === activePatientId ? 'background: rgba(2, 132, 199, 0.05);' : ''}">
                <td><strong>${ins.nombre}</strong></td>
                <td>${ins.presentacion}</td>
                <td><strong style="font-size: 1.1rem; font-family: var(--font-mono);">${ins.stockActual}</strong> un.</td>
                <td>~${ins.consumoDiarioEstimado}/día</td>
                <td>
                  <strong style="color: ${ins.diasDisponibles <= 2 ? 'var(--danger-600)' : ins.diasDisponibles <= 5 ? 'var(--warning-600)' : 'var(--success-600)'}; font-size: 1rem;">
                    ${ins.diasDisponibles} días
                  </strong>
                </td>
                <td>
                  <span class="badge ${ins.severidad === 'danger' ? 'badge-danger' : ins.severidad === 'warning' ? 'badge-warning' : 'badge-success'}">
                    ${ins.estadoAlerta}
                  </span>
                </td>
                <td>
                  <button class="btn btn-secondary btn-sm btn-reponer-insumo" data-id="${ins.id}" style="font-size: 0.75rem;">
                    + Reponer Stock
                  </button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>

    <!-- 4. GESTIÓN DE TRASLADOS SANITARIOS -->
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">
          🚑 Seguimiento de Traslados Sanitarios y Ambulancias
        </h3>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Fecha y Hora</th>
              <th>Paciente</th>
              <th>Tipo de Móvil</th>
              <th>Origen y Destino</th>
              <th>Motivo de Derivación</th>
              <th>Empresa Prestadora</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            ${traslados.map(t => `
              <tr>
                <td><strong>${t.fechaHora}</strong></td>
                <td>${t.pacienteNombre}</td>
                <td><span class="badge badge-primary">${t.tipoMovil}</span></td>
                <td style="font-size: 0.8rem;">
                  <div><strong>Desde:</strong> ${t.origen}</div>
                  <div><strong>Hacia:</strong> ${t.destino}</div>
                </td>
                <td style="font-size: 0.825rem;">${t.motivo}</td>
                <td>${t.empresa}</td>
                <td><span class="badge badge-warning">${t.estado}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;

  // Listeners
  container.querySelectorAll('.btn-reponer-insumo').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const id = e.currentTarget.dataset.id;
      const ins = allInsumos.find(i => i.id === id);
      if (ins) {
        ins.stockActual += 10;
        ins.diasDisponibles = Math.round(ins.stockActual / (ins.consumoDiarioEstimado || 1));
        ins.estadoAlerta = 'Stock Normal';
        ins.severidad = 'success';
        await offlineStore.put('insumos', ins);
        notify.success(`Stock de ${ins.nombre} incrementado (+10 unidades).`);
        renderLogisticaView(container, activePatientId);
      }
    });
  });

  const btnAmbulancia = document.getElementById('btn-solicitar-traslado');
  if (btnAmbulancia) {
    btnAmbulancia.addEventListener('click', () => {
      notify.info('Solicitud de derivación en ambulancia enviada a la central de despacho.');
    });
  }

  const btnEnvio = document.getElementById('btn-nuevo-envio');
  if (btnEnvio) {
    btnEnvio.addEventListener('click', () => {
      notify.success('Orden de entrega de descartables generada con remito oficial.');
    });
  }
}
